import {useEffect, useState, useSyncExternalStore} from "react";

// One snapshot per controller notification; components re-render exactly when the
// old render() would have repainted. The third argument only exists so the tree can
// also be rendered by react-dom/server in smoke checks.
export function useGame(controller) {
  return useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
}

// Alerts show "recent" events and must also disappear on their own. Re-rendering
// once shortly after the newest event ages out replaces the old 5.1s repaint timers.
// Keyed on the alert's identity, not on nothing: an unkeyed effect re-armed the timer
// on every render, so a poll landing once a second pushed the expiry away forever.
// The card notice lives longer than a drink alert, so the wait is a parameter.
export function useAlertExpiry(alertKey, lifetime = 5100) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!alertKey) return undefined;
    const timer = setTimeout(() => setTick((tick) => tick + 1), lifetime);
    return () => clearTimeout(timer);
  }, [alertKey, lifetime]);
}

// An element's rendered size, kept current by a ResizeObserver, or null until the first
// measurement. The board measures its arena with it, so the controller can size the
// seats to the room this screen actually gives them rather than to the floor.
export function useElementSize(ref) {
  const [size, setSize] = useState(null);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);
      setSize((last) => (last?.width === width && last?.height === height ? last : {width, height}));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

// Re-renders once a second while `active`, for a countdown whose answer the controller
// reads off the clock. Only the small component that shows it ticks, not the screen.
export function useTick(active, every = 1000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setTick((tick) => tick + 1), every);
    return () => clearInterval(timer);
  }, [active, every]);
}
